/**
 * localStorage persistence for the checkout flow's Chargebee `payment_intent`.
 *
 * The slice keeps the full `payment_intent` object in Redux memory. On page
 * refresh, the slice resets to initial state — we recover by reading the PI
 * back from localStorage, keyed by `payment_id` (which the URL preserves).
 *
 * We do NOT recover via `refresh-payment-intent`: the BE rejects that endpoint
 * when called with only `{ payment_id }` (no `payment_source_id`, no
 * `coupon_id`). That endpoint stays in use for coupon / saved-card refresh
 * flows where the BE accepts the call.
 *
 * On cache miss (cleared, different browser, expired), the recovery thunk
 * falls back to the auto-mint UX (fresh `prepareCheckoutIntent` + soft inline
 * banner). All operations soft-fail on localStorage exceptions (incognito,
 * quota, etc.).
 */

const KEY_PREFIX = "checkout:pi:";
const KEY = (paymentId: string): string => `${KEY_PREFIX}${paymentId}`;

/**
 * TTL for a persisted PI. Chargebee PIs typically live ~15 minutes; 30 minutes
 * here is a comfortable upper bound. An expired cache entry is treated as a
 * miss and triggers the auto-mint fallback.
 */
const TTL_MS = 30 * 60 * 1000;

interface CacheEntry<T> {
  pi: T;
  /**
   * Billing entity collecting this checkout ("inc" | "pc"), from the top-level
   * `gateway` on the checkout response. Cached alongside the PI so a refresh
   * restores saved-card eligibility without another round trip. Absent on
   * entries written before gateway scoping shipped — those read back as
   * `null`, which `normalizeGateway` treats as "inc".
   */
  gateway?: string | null;
  at: number;
}

export const persistPaymentIntent = <T>(paymentId: string, pi: T, gateway?: string | null): void => {
  if (!paymentId || !pi) return;
  try {
    const entry: CacheEntry<T> = { pi, gateway: gateway ?? null, at: Date.now() };
    localStorage.setItem(KEY(paymentId), JSON.stringify(entry));
  } catch {
    // localStorage may be unavailable (incognito / quota exceeded).
    // Soft-fail; recovery falls back to auto-mint on cache miss.
  }
};

export const readPersistedPaymentIntent = <T = unknown>(
  paymentId: string
): T | null => {
  if (!paymentId) return null;
  try {
    const raw = localStorage.getItem(KEY(paymentId));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry<T>;
    if (!entry || typeof entry.at !== "number") {
      localStorage.removeItem(KEY(paymentId));
      return null;
    }
    if (Date.now() - entry.at > TTL_MS) {
      localStorage.removeItem(KEY(paymentId));
      return null;
    }
    return entry.pi;
  } catch {
    return null;
  }
};

/**
 * The cached billing entity for a persisted checkout, or `null` when the entry
 * is missing, expired, or predates gateway scoping.
 *
 * Deliberately a separate reader rather than a widened return on
 * `readPersistedPaymentIntent`: that function's contract (PI or null) is
 * consumed by the recovery thunk and mocked across the checkout test suites.
 */
export const readPersistedGateway = (paymentId: string): string | null => {
  if (!paymentId) return null;
  try {
    const raw = localStorage.getItem(KEY(paymentId));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry<unknown>;
    if (!entry || typeof entry.at !== "number") return null;
    if (Date.now() - entry.at > TTL_MS) return null;
    return entry.gateway ?? null;
  } catch {
    return null;
  }
};

export const clearPersistedPaymentIntent = (paymentId: string | null): void => {
  if (!paymentId) return;
  try {
    localStorage.removeItem(KEY(paymentId));
  } catch {
    // Soft-fail.
  }
};

// ─── Consultation description (consultation-contract-rev3) ──────────────────
//
// The shelf's consultation description lives in Redux (`checkoutFlow.
// additionalInfo`), which resets on page refresh — but the payment itself
// recovers from the URL + the PI cache above, so a refreshed checkout would
// lose the user's typed notes and charge with the "User provided no details"
// fallback instead (INH-4988 made the description optional; the fallback
// replaced the old rev-3 400/guard). Persist the description at shelf-CTA
// click and restore it during URL hydration so real notes survive.
//
// One fixed key (there is no payment_id yet at CTA-click time). The value
// embeds the originating threadId; `readConsultationDescription` returns the
// text only for a matching thread, so a stale entry from another thread can
// never leak into a different consultation's charge.

const DESCRIPTION_KEY = "checkout:consultation-description";

interface DescriptionEntry {
  threadId: string | null;
  description: string;
  at: number;
}

export const persistConsultationDescription = (arg: {
  threadId: string | null;
  description: string;
}): void => {
  if (!arg.description?.trim()) return;
  try {
    const entry: DescriptionEntry = {
      threadId: arg.threadId ?? null,
      description: arg.description,
      at: Date.now(),
    };
    localStorage.setItem(DESCRIPTION_KEY, JSON.stringify(entry));
  } catch {
    // Soft-fail; the checkout submit guard catches the missing-notes case.
  }
};

export const readConsultationDescription = (threadId: string | null): string | null => {
  try {
    const raw = localStorage.getItem(DESCRIPTION_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as DescriptionEntry;
    if (!entry || typeof entry.at !== "number" || typeof entry.description !== "string") {
      localStorage.removeItem(DESCRIPTION_KEY);
      return null;
    }
    if (Date.now() - entry.at > TTL_MS) {
      localStorage.removeItem(DESCRIPTION_KEY);
      return null;
    }
    if ((entry.threadId ?? null) !== (threadId ?? null)) return null;
    return entry.description;
  } catch {
    return null;
  }
};

export const clearConsultationDescription = (): void => {
  try {
    localStorage.removeItem(DESCRIPTION_KEY);
  } catch {
    // Soft-fail.
  }
};
